import { useEffect, useState, useCallback, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import {
  ResourceFormData,
  resourceFormSchema,
} from "@/modules/hub/features/ResourceManagement/hooks/useCreateResource/types";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { getCreateUpdateResourceMetadataAction } from "@/modules/hub/features/ResourceManagement/slices/resourceManagementMetadataSlice";
import {
  getResourceAction,
  updateResourceAction,
} from "@/modules/hub/features/ResourceManagement/slices/createUpdateResourceSlice";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@/context/NotificationContext";
import { slugify } from "@/utils/slugify";
import { ResourceTypes } from "@/modules/hub/features/ResourceManagement/hooks/useCreateResource/types";

export function useEditResource(uuid: string | undefined) {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const {
    resourceTags,
    resourceCategories,
    businessRoles,
    trades,
    resourceTypes,
    loading: metadataLoading,
    error: metadataError,
  } = useAppSelector((state) => state.resourceManagementMetadataReducer);
  const { fetchedResource, loadingUpdate, loadingGet } = useAppSelector(
    (state) => state.createUpdateResource,
  );

  useEffect(() => {
    dispatch(getCreateUpdateResourceMetadataAction());
  }, [dispatch]);

  const defaultValues: ResourceFormData = {
    title: "",
    slug: "",
    searchDescription: "",
    type: null,
    content_url: "",
    fileUuid: null,
    thumbnailFileUuid: null,
    publish_start_date: null,
    publish_end_date: null,
    is_published: false,
    is_featured: false,
    requiresEaglesAccess: false,
    requiresLATAAccess: false,
    hiddenFromLATASubscribers: false,
    tags: [],
    categories: [],
    tradeIds: [],
    roleIds: [],
    contentBlocks: [],
    relatedResources: [],
    seriesEntries: [],
  };

  const form = useForm<ResourceFormData>({
    resolver: zodResolver(resourceFormSchema),
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (fetchedResource) {
      form.reset({
        title: fetchedResource.title || "",
        slug: fetchedResource.slug || "",
        searchDescription: fetchedResource.searchDescription || "",
        type: fetchedResource.typeId || 0,
        content_url: fetchedResource.contentUrl || "",
        thumbnailFileUuid: fetchedResource.thumbnailFileUuid || null,
        publish_start_date: fetchedResource.publishStartDate
          ? new Date(fetchedResource.publishStartDate)
          : null,
        publish_end_date: fetchedResource.publishEndDate
          ? new Date(fetchedResource.publishEndDate)
          : null,
        is_published: fetchedResource.isPublished ?? false,
        is_featured: fetchedResource.isFeatured ?? false,
        requiresEaglesAccess: fetchedResource.requiresEaglesAccess ?? false,
        requiresLATAAccess: fetchedResource.requiresLATAAccess ?? false,
        hiddenFromLATASubscribers:
          fetchedResource.hiddenFromLATASubscribers ?? false,
        tags:
          fetchedResource.tags?.map((t) => ({ id: t.id, name: t.name })) || [],
        categories:
          fetchedResource.categories?.map((c) => ({
            id: c.id,
            name: c.name,
          })) || [],
        tradeIds: fetchedResource.tradeIds?.map(String) || [],
        roleIds: fetchedResource.roleIds?.map(String) || [],
        contentBlocks:
          fetchedResource.contentBlocks?.map((block) => ({
            id: block.id,
            type: block.type,
            content: block.content,
            order_number: block.order_number,
            metadata: {},
            fileUuid: block.file?.uuid || null,
            file: block.file, // Pass the entire file object for display
            title: block.title || undefined,
            shortDescription: block.short_description || undefined,
            // Include resource references for resource_reference blocks
            resourceReferenceIds:
              block.referenced_resources?.map((r) => r.id) || undefined,
            referencedResources:
              block.referenced_resources?.map((r) => ({
                id: r.id,
                name: r.title,
                thumbnailUrl: r.thumbnailUrl,
                primaryIcon: r.primaryIcon || null,
                resourceType: r.resourceType,
              })) || undefined,
            // Include trade IDs if they exist
            tradeIds: block.trade_ids || [],
          })) || [],
        relatedResources:
          fetchedResource.relatedResources?.map((rr) => ({
            id: rr.id,
            name: rr.title,
          })) || [],
        fileUuid: fetchedResource.file?.uuid || null,
        seriesEntries:
          fetchedResource.seriesEntries?.map((se, index) => ({
            id: se.id,
            name: se.title,
            sortOrder: se.sortOrder ?? index,
            thumbnailUrl: se.thumbnailUrl || null,
            resourceType: se.resourceType || null,
          })) || [],
      });
    }
  }, [fetchedResource, form]);

  const titleValue = form.watch("title") || "";
  useEffect(() => {
    const autoSlug = titleValue
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    form.setValue("slug", autoSlug, { shouldDirty: true });
  }, [titleValue, form]);

  const resourceTypeId = form.watch("type");
  const selectedResourceType = useMemo(() => {
    if (!resourceTypeId) return null;
    return resourceTypes.find((rt) => rt.id === resourceTypeId) || null;
  }, [resourceTypeId, resourceTypes]);

  // Clear content blocks when Partner Page is selected
  useEffect(() => {
    if (selectedResourceType?.name === ResourceTypes.PARTNER_PAGE) {
      const currentBlocks = form.getValues("contentBlocks");
      if (currentBlocks && currentBlocks.length > 0) {
        form.setValue("contentBlocks", [], { shouldDirty: true });
      }
    }
  }, [selectedResourceType, form]);

  const submitForm = useCallback(
    async (values: ResourceFormData, shouldRedirect: boolean = false) => {
      try {
        setIsLoading(true);
        if (!fetchedResource) {
          throw new Error("No resource loaded to update");
        }
        const numericType = values.type ?? 0;

        // Validation based on resource type
        const resourceTypeName = selectedResourceType?.name || "";

        // Validate required content_url for Video, Podcast, Partner Page
        if (
          [
            ResourceTypes.VIDEO,
            ResourceTypes.PODCAST,
            ResourceTypes.PARTNER_PAGE,
          ].includes(resourceTypeName as any)
        ) {
          if (!values.content_url) {
            toast({
              title: "Validation Error",
              description: `Content URL is required for ${resourceTypeName} resources`,
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
        }

        // Validate required file for File type
        if (
          resourceTypeName === ResourceTypes.FILE &&
          !values.fileUuid &&
          !fetchedResource.file
        ) {
          toast({
            title: "Validation Error",
            description: "File is required for File type resources",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // Clear content blocks for Partner Page
        const finalBlocks =
          resourceTypeName === ResourceTypes.PARTNER_PAGE
            ? []
            : (values.contentBlocks || []).map((b) => ({
                ...b,
                order_number: b.order_number ?? 0,
                fileUuid: b.fileUuid ?? null,
                title: b.title || undefined,
                // Transform shortDescription to short_description for API
                short_description: b.shortDescription || undefined,
                shortDescription: undefined, // Remove the camelCase version
                // Include tradeIds if they exist
                tradeIds: b.tradeIds || [],
              }));

        const publishStart = values.publish_start_date
          ? values.publish_start_date.toISOString()
          : null;
        const publishEnd = values.publish_end_date
          ? values.publish_end_date.toISOString()
          : null;
        const tagIds = (values.tags || []).map((t) => t.id);
        const categoryIds = (values.categories || []).map((c) => c.id);
        const relatedResourceIds = (values.relatedResources || []).map(
          (r) => r.id,
        );

        // Convert seriesEntries to seriesResourceIds for API
        const seriesResourceIds = (values.seriesEntries || []).map((s) => s.id);

        const finalData = {
          slug: values.slug ? slugify(values.slug) : null,
          title: values.title,
          searchDescription: values.searchDescription,
          type: numericType,
          content_url: values.content_url || null,
          fileUuid: values.fileUuid || null,
          thumbnailFileUuid: values.thumbnailFileUuid ?? null,
          publish_start_date: publishStart,
          publish_end_date: publishEnd,
          is_published: values.is_published ?? false,
          is_featured: values.is_featured ?? false,
          requiresEaglesAccess: values.requiresEaglesAccess ?? false,
          requiresLATAAccess: values.requiresLATAAccess ?? false,
          hiddenFromLATASubscribers: values.hiddenFromLATASubscribers ?? false,
          tradeIds: values.tradeIds || [],
          roleIds: values.roleIds || [],
          tagIds,
          categoryIds,
          relatedResourceIds,
          seriesResourceIds,
          contentBlocks: finalBlocks,
        };
        const resourceId = fetchedResource.id;
        if (!resourceId) {
          throw new Error("No resource ID found to update");
        }
        await dispatch(
          updateResourceAction(resourceId, finalData, () => {
            showNotification(
              "Success!",
              "Resource has been successfully updated!",
              "success",
            );
            if (shouldRedirect) {
              navigate(`/admin/resources`);
            }
          }),
        );
      } catch (error) {
        console.error("Error updating resource:", error);
        if (error instanceof Error) {
          toast({
            title: "Error",
            description: error.message || "Failed to update",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: "An unknown error occurred while updating.",
            variant: "destructive",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      dispatch,
      fetchedResource,
      toast,
      navigate,
      showNotification,
      selectedResourceType,
    ],
  );

  const handleCancel = useCallback(() => {
    navigate(`/admin/resources`);
  }, [navigate]);

  useEffect(() => {
    if (uuid) {
      dispatch(getResourceAction(uuid));
    }
  }, [uuid, dispatch]);

  return {
    form,
    isLoading: isLoading || loadingUpdate,
    tags: resourceTags,
    trades,
    categories: resourceCategories,
    roles: businessRoles,
    resourceTypes,
    metadataLoading,
    metadataError,
    resourceTypeId,
    selectedResourceType,
    submitForm,
    handleCancel,
    loadingGet,
    resourceTitle: fetchedResource?.title,
    fetchedResource,
  };
}
